// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import ListItem from "@insite/mobius/Lists/ListItem";
import UnorderedList from "@insite/mobius/Lists/UnorderedList";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import scrollToId from "@toolkit/Framework/Common/Utilities/scrollToId";
import { FormContextProvider } from "@toolkit/Framework/Components/FormContext/FormContextProvider";
import {
    useGetFirstInputInError,
    useGetInputs,
    useHasErrorInForm,
    useInitForm,
} from "@toolkit/Framework/Store/Toolkit/Components/Form/FormHooks";
import setHasAlreadyErrors from "@toolkit/Framework/Store/Toolkit/Components/Form/Handlers/SetHasAlreadyErrors";
import { focusStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, PropsWithChildren, useEffect, useRef, useState } from "react";
import { css } from "styled-components";

const errorWrapperPrefix = "form-error-wrapper";
const errorLinkPrefix = "form-error-link";

export interface FormWrapperProps extends ExpandStyles<FormWrapperStyles> {
    generalErrorMessage?: string;
    id?: string | number;
    onlyFocus?: boolean;
    delayed?: boolean;
    noFocusNoScroll?: boolean; // Store Custom Code
}

export interface FormWrapperStyles {
    wrapper?: InjectableCss;
    errorsInfosBox?: InfosBoxProps;
    errorsWrapper?: InjectableCss;
    errorsListTitle?: TypographyPresentationProps;
    errorsList?: InjectableCss;
    errorsListItem?: InjectableCss;
    errorsLink?: LinkPresentationProps;
    errorsGeneralMessageText?: TypographyPresentationProps;
}

export const formWrapperStyles: FormWrapperStyles = {
    errorsInfosBox: {
        type: "error",
        extendedStyles: {
            icon: {
                css: css`
                    align-self: flex-start;
                `,
            },
        },
    },
    errorsListTitle: {
        css: css`
            font-weight: ${({ theme }) => theme.design.typography.weight.bold};
        `,
    },
    errorsLink: {
        css: linkStyles("inherit", "inherit", true, true),
    },
    errorsGeneralMessageText: {
        css: css`
            ${focusStyles()}
        `,
    },
};

const FormWrapper: FC<PropsWithChildren<FormWrapperProps>> = ({
    extendedStyles,
    overridedStyles,
    generalErrorMessage,
    onlyFocus,
    noFocusNoScroll, // Store Custom Code
    children,
    id,
    delayed,
}) => {
    const styles = useExpandProps(formWrapperStyles, extendedStyles, overridedStyles);
    const [uid] = useState(id?.toString() || uniqueId());
    const { form } = useInitForm();
    const dispatch = useAppDispatch();
    const formId = form.id;
    const inputs = useGetInputs(formId);
    const firstInputInError = useGetFirstInputInError(formId);
    const ref = useRef<HTMLDivElement>(null);
    const formWrapperInfosBoxId = `${errorWrapperPrefix}-${uid}`;
    const formErrorTitleId = `form-error-title-${uid}`;
    const formErrorListId = `form-error-list-${uid}`;
    const formErrorGeneralMessageId = `form-error-general-message-${uid}`;

    const baseHasError = useHasErrorInForm(formId);
    const hasErrorRef = useRef(false);
    const [delayedHasError, setDelayedHasError] = useState(false);
    const hasError = delayed ? delayedHasError : baseHasError;

    useEffect(() => {
        if (delayed) {
            /* Add a delay to allow form state changes to fully process and prevent visual flicker */
            hasErrorRef.current = baseHasError;
            setTimeout(() => {
                if (delayedHasError !== hasErrorRef.current) {
                    setDelayedHasError(baseHasError);
                }
            });
        }
    }, [baseHasError]);

    useEffect(() => {
        if (ref.current && hasError) {
            if (!form.hasAlreadyError) {
                dispatch(setHasAlreadyErrors({ formId }));
                if (firstInputInError && document.activeElement?.getAttribute("id") !== firstInputInError.id) {
                    scrollToError(`${errorLinkPrefix}-${firstInputInError?.id}`);
                }
            }
        }
    }, [hasError, ref.current]);

    useEffect(() => {
        if (generalErrorMessage) {
            scrollToError();
        }
    }, [generalErrorMessage]);

    const scrollToError = (linkId?: string) => {
        if (ref.current) {
            const activeElementId = document.activeElement?.getAttribute("id");

            if (activeElementId && [errorWrapperPrefix, errorLinkPrefix].some(x => activeElementId.startsWith(x))) {
                return;
            }
            /* Store Custom Code - Start */
            if (noFocusNoScroll !== true) {
                scrollToId(ref.current, { onlyFocus, padding: 16 });
                ((linkId && document.getElementById(linkId)) || ref.current).focus();
            }
            /* Store Custom Code - End */
        }
    };

    const scrollToField = (id: string) => {
        if (ref.current) {
            scrollToId(id, {
                focus: true,
                onlyFocus,
                padding: 30,
            });
        }
    };

    const errorContent = () => {
        const errors = inputs.filter(x => x.error).length;
        const errorMessage =
            errors > 1 || (errors && generalErrorMessage)
                ? translate("Some errors have occured. Please address the following errors and re-submit the form.")
                : translate("An error has occured. Please address the following error and re-submit the form.");

        return (
            <StyledWrapper
                {...styles.errorsWrapper}
                ref={ref}
                id={formWrapperInfosBoxId}
                aria-describedby={formErrorListId}
            >
                <Typography id={formErrorTitleId} {...styles.errorsListTitle}>
                    {errorMessage}
                </Typography>
                <UnorderedList id={formErrorListId} aria-labelledby={formErrorTitleId} {...styles.errorsList}>
                    {generalErrorMessage ? (
                        <ListItem key={formErrorGeneralMessageId} {...styles.errorsListItem}>
                            <Typography
                                {...styles.errorsGeneralMessageText}
                                id={formErrorGeneralMessageId}
                                tabIndex={0}
                            >
                                {generalErrorMessage}
                            </Typography>
                        </ListItem>
                    ) : null}
                    {inputs.map(({ error, id }) => {
                        if (error) {
                            return (
                                <ListItem key={id} {...styles.errorsListItem}>
                                    <Link
                                        id={`${errorLinkPrefix}-${id}`}
                                        onClick={() => scrollToField(id)}
                                        {...styles.errorsLink}
                                    >
                                        {error}
                                    </Link>
                                </ListItem>
                            );
                        }
                        return null;
                    })}
                </UnorderedList>
            </StyledWrapper>
        );
    };

    return (
        <FormContextProvider form={form}>
            <StyledWrapper {...styles.wrapper}>
                {(hasError || generalErrorMessage) && (
                    <InfosBox {...styles.errorsInfosBox} id={formWrapperInfosBoxId} ref={ref} role="alert">
                        {errorContent()}
                    </InfosBox>
                )}
                {children}
            </StyledWrapper>
        </FormContextProvider>
    );
};

export default FormWrapper;
